<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import type { ServerListItem } from './lib/protocol';
  import { randomClaimSecret } from './lib/directory';
  import { installCommand, scanCommand as scanCommandFor, parseClaim, parseServerLink, waitForServer, ClaimTimeout, type RunChoice } from './lib/onboard';
  import { decodeQrFile, startCameraScan } from './lib/scan';
  import { wsUrl } from './lib/servers';
  import { Dialog } from './lib/ui';

  export let origin: string;
  /** A secret that arrived by link or earlier scan: start waiting for that server right away. */
  export let prefillSecret: string | null = null;
  export let onclose: () => void;
  export let onadd: (server: ServerListItem) => void;
  /** A scanned or pasted re-add link for a server that is already registered. */
  export let onserver: (serverId: string) => void;

  type Tab = 'paste' | 'scan' | 'url';
  let tab: Tab = prefillSecret ? 'paste' : 'paste';
  let name = '';
  let secret = prefillSecret ?? randomClaimSecret();
  /** Where sessions run. The host choices give this browser a shell on the machine itself, so they need consent. */
  let where: 'container' | 'host' = 'container';
  type HostAs = 'user' | 'me' | 'root';
  const hostMode: Record<HostAs, RunChoice> = { user: 'host-user', me: 'host-me', root: 'host' };
  let hostAs: HostAs = 'user';
  let consent = false;
  $: mode = where === 'container' ? 'container' : hostMode[hostAs];
  $: if (where === 'container' || hostAs) consent = false; // each choice of a host mode is agreed to on its own
  $: allowed = mode === 'container' || consent;
  $: command = installCommand(origin, secret, mode);
  let waiting = false;
  let error = '';
  let copied = false;
  let manualUrl = '';
  let manualClaim = '';
  let video: HTMLVideoElement;
  let stopCamera: (() => void) | null = null;
  let scanning = false;
  let abort: AbortController | null = null;

  async function begin(s: string) {
    abort?.abort();
    abort = new AbortController();
    secret = s;
    waiting = true;
    error = '';
    try {
      const record = await waitForServer(origin, s, abort.signal);
      onadd({ server_id: record.server_id, server_name: name.trim(), server_url: '', record, registered_at: Date.now(), claim: s });
    } catch (e) {
      waiting = false;
      if (e instanceof DOMException && e.name === 'AbortError') return;
      error = e instanceof ClaimTimeout ? 'Nothing arrived within 15 minutes. Start again to get a new command.' : e instanceof Error ? e.message : 'Failed';
    }
  }

  $: scanCommand = scanCommandFor(origin, mode);

  /** A tap selects the whole command, so it can also be copied by hand on browsers without clipboard access. */
  function selectAll(e: MouseEvent) {
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(e.currentTarget as Node);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  async function copy(text: string) {
    await navigator.clipboard.writeText(text);
    copied = true;
    setTimeout(() => (copied = false), 2000);
  }

  function gotText(text: string) {
    const known = parseServerLink(text);
    if (known) {
      stopScan();
      onserver(known);
      return;
    }
    const s = parseClaim(text);
    if (!s) {
      error = 'That QR is not a Termanch code.';
      return;
    }
    stopScan();
    void begin(s);
  }

  async function startScan() {
    error = '';
    scanning = true;
    stopCamera = await startCameraScan(video, gotText, (m) => {
      error = m;
      scanning = false;
    });
  }

  function stopScan() {
    stopCamera?.();
    stopCamera = null;
    scanning = false;
  }

  async function onFile(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    error = '';
    try {
      const text = await decodeQrFile(file);
      if (text) gotText(text);
      else error = 'No QR code found in that image. Make sure the whole QR is visible, then try again.';
    } catch {
      error = 'Could not read that image.';
    }
  }

  function addByUrl() {
    try {
      wsUrl(manualUrl);
    } catch {
      error = 'Enter the server URL (https://...)';
      return;
    }
    onadd({ server_url: manualUrl.trim(), server_name: name.trim(), registered_at: Date.now(), claim: manualClaim.trim() || undefined });
  }

  function choose(t: Tab) {
    stopScan();
    error = '';
    tab = t;
  }

  onMount(() => {
    // Flow A starts waiting immediately: the secret is already in the command the user is about to paste.
    void begin(secret);
  });
  onDestroy(() => {
    abort?.abort();
    stopScan();
  });
</script>

<Dialog title="Add Server" {onclose}>
    <div style="display: flex; gap: 8px; margin-bottom: 16px;">
      <button class={tab === 'paste' ? '' : 'secondary'} on:click={() => choose('paste')} style="flex: 1; padding: 8px;">Paste a command</button>
      <button class={tab === 'scan' ? '' : 'secondary'} on:click={() => choose('scan')} style="flex: 1; padding: 8px;">Scan a QR</button>
    </div>

    <label style="display: block; font-size: 0.875rem; margin-bottom: 6px; color: var(--fg-muted);">Name</label>
    <input bind:value={name} placeholder="Leave empty to use the server's hostname" maxlength="40" style="width: 100%; margin-bottom: 16px;" />

    <fieldset class="where">
      <legend>Where should sessions run?</legend>
      <label class="choice">
        <input type="radio" bind:group={where} value="container" />
        <span><strong>In an isolated container</strong> (recommended)<br /><small>Sessions run in a Docker container and cannot see the rest of your VPS. Docker is installed if missing.</small></span>
      </label>
      <label class="choice">
        <input type="radio" bind:group={where} value="host" />
        <span><strong>On the machine itself</strong> (no Docker)<br /><small>Sessions run directly on your VPS, where your repos and agent tools already are.</small></span>
      </label>
      {#if where === 'host'}
        <div class="sub">
          <label class="choice">
            <input type="radio" bind:group={hostAs} value="user" />
            <span>As a dedicated user <code>termanch</code> (recommended)<br /><small>Created for you. It can only touch its own files and what you give it access to.</small></span>
          </label>
          <label class="choice">
            <input type="radio" bind:group={hostAs} value="me" />
            <span>As the user who runs the command<br /><small>Sessions open as you, in your home, with your repos, keys and agent logins. Nothing new is created.</small></span>
          </label>
          <label class="choice">
            <input type="radio" bind:group={hostAs} value="root" />
            <span>As <code>root</code><br /><small>Full control of the machine.</small></span>
          </label>
        </div>
        <div class="warning" role="note">
          <strong>This gives your browser a shell on your server.</strong>
          Anything you run in a session can read and change this machine's files and run programs as {hostAs === 'root' ? 'root, so it can change anything' : hostAs === 'me' ? 'you, with access to everything you can reach, including your SSH keys and agent logins' : 'the termanch user'}.
          Only continue on a machine you own. The installer lists every change it makes first and can undo them all.
        </div>
        <label class="choice">
          <input type="checkbox" bind:checked={consent} />
          <span>I understand and want sessions to run on this machine{hostAs === 'root' ? ' as root' : hostAs === 'me' ? ' as my own user' : ''}.</span>
        </label>
      {/if}
    </fieldset>

    {#if tab === 'paste'}
      <p style="color: var(--fg-muted); margin-bottom: 8px;">Run this once on your VPS (any Linux with root access). It installs and starts everything, and the server appears here by itself.</p>
      {#if allowed}
        <pre class="command" on:click={selectAll}>{command}</pre>
        <button on:click={() => copy(command)} style="width: 100%; margin-bottom: 12px;">{copied ? 'Copied' : 'Copy command'}</button>
      {:else}
        <p class="locked">Tick the box above to see the command.</p>
      {/if}
    {:else if tab === 'scan'}
      <p style="color: var(--fg-muted); margin-bottom: 8px;">On a new VPS run this. It shows a QR code. Scan it here, or take a screenshot of it and choose the screenshot. Delete the screenshot afterwards.</p>
      {#if allowed}
        <pre class="command" on:click={selectAll}>{scanCommand}</pre>
        <button class="secondary" on:click={() => copy(scanCommand)} style="width: 100%; margin-bottom: 12px;">{copied ? 'Copied' : 'Copy command'}</button>
      {:else}
        <p class="locked">Tick the box above to see the command.</p>
      {/if}
      <video bind:this={video} muted playsinline style="width: 100%; border-radius: 8px; background: #000; margin-bottom: 8px; display: {scanning ? 'block' : 'none'};"></video>
      <div style="display: flex; gap: 8px; margin-bottom: 12px;">
        {#if scanning}
          <button class="secondary" on:click={stopScan} style="flex: 1;">Stop camera</button>
        {:else}
          <button on:click={startScan} style="flex: 1;">Scan with camera</button>
        {/if}
        <label class="secondary" style="flex: 1; text-align: center; cursor: pointer; padding: 10px; border: 1px solid var(--border); border-radius: 8px;">
          Choose screenshot
          <input type="file" accept="image/*" on:change={onFile} style="display: none;" />
        </label>
      </div>
    {/if}

    {#if waiting}
      <p style="color: var(--fg-muted); font-size: 0.875rem;">Waiting for your server to appear… this can take a minute.</p>
    {/if}
    {#if error}
      <p style="color: var(--danger); margin: 8px 0; font-size: 0.875rem;">{error}</p>
    {/if}

    <details style="margin-top: 16px;">
      <summary style="cursor: pointer; color: var(--fg-muted); font-size: 0.875rem;">Advanced: connect by URL (tunnel or reverse proxy)</summary>
      <input bind:value={manualUrl} placeholder="https://xxxx.trycloudflare.com" style="width: 100%; margin: 8px 0;" />
      <input bind:value={manualClaim} placeholder="Registration token (first connection only)" style="width: 100%; margin-bottom: 8px;" />
      <button class="secondary" on:click={addByUrl} disabled={!manualUrl} style="width: 100%;">Add and connect</button>
    </details>
</Dialog>

<style>
  .where { border: 1px solid var(--border); border-radius: 10px; padding: 12px; margin: 0 0 16px; }
  .where legend { padding: 0 6px; font-size: 0.875rem; color: var(--fg-muted); }
  .choice { display: flex; gap: 10px; align-items: flex-start; margin: 6px 0; cursor: pointer; }
  .choice input { margin-top: 4px; }
  .choice small { color: var(--fg-muted); }
  .sub { margin-left: 24px; border-left: 2px solid var(--border); padding-left: 12px; }
  .warning { margin: 10px 0; padding: 10px 12px; border: 1px solid var(--danger); border-radius: 8px; font-size: 0.8rem; background: rgba(248, 81, 73, 0.08); }
  .locked { color: var(--fg-muted); font-size: 0.85rem; margin: 0 0 12px; }
  .command { background: var(--bg); border: 1px solid var(--border); border-radius: 8px; padding: 12px; font-family: monospace; font-size: 0.75rem; white-space: pre-wrap; overflow-wrap: anywhere; user-select: all; cursor: copy; margin: 0 0 8px; }
</style>
